/**
 * Stripe payouts — the money's other side, on Billing.
 *
 * Owner, Oct 31 2026: "Any way to connect stripe so I can see when payouts
 * reach etc. how much has been paid what's in transit all that?" The app
 * already charges cards and takes Checkout through Stripe; this card reads
 * back (server/services/stripePayouts, read only): the balance right now,
 * what is on the way and the day it lands in the bank, the payout schedule,
 * the months' takings and fees, and every payout of the last 90 days — opened,
 * the charges in it, each matched to the customer and the bill it paid.
 * Nothing here moves money; the Stripe dashboard does that.
 */
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { ChevronDown, ExternalLink, Landmark, RefreshCw } from 'lucide-react';
import { api } from '../services/api';
import { fmtTs } from '../utils/time';
import FoldCard from './FoldCard';

interface PayoutRow {
  id: string; amount: number; currency: string; status: string; words: string;
  arrival: string | null; created: string | null; method: string | null; type: string | null;
  automatic: boolean; description: string | null; failure: string | null;
}
interface MonthRow { month: string; label: string; gross: number; refunds: number; fees: number; net: number; count: number; }
interface Overview {
  configured: boolean; words?: string; mode?: string | null; days: number; fetched_at?: string;
  balance?: { available: number; pending: number; currency: string };
  schedule?: string | null; bank?: { bank: string | null; last4: string | null } | null;
  in_transit?: number; in_transit_count?: number; paid_out?: number; paid_count?: number;
  last_paid?: { amount: number; arrival: string | null; id: string } | null;
  next?: { amount: number; arrival: string | null; id: string; words: string } | null;
  failed?: { id: string; amount: number; arrival: string | null; failure: string | null }[];
  payouts: PayoutRow[]; months: MonthRow[]; errors?: string[];
}
interface DetailLine {
  id: string; type: string; amount: number; fee: number; net: number; created: string | null; description: string | null;
  pi: string | null; charge: string | null; card: { brand: string; last4: string } | null;
  invoice_id: number | null; invoice_number: string | null; customer_id: number | null; customer_name: string | null; tip: number;
}
interface Detail { payout: PayoutRow; lines: DetailLine[]; totals: { gross: number; fees: number; net: number; lines: number }; }

const money = (n: any) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** "Oct 29" from a YYYY-MM-DD (a payout's arrival day is a date, not an instant). */
const day = (d?: string | null) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
const when = (ts?: string | null) => fmtTs(ts, { month: 'short', day: 'numeric' });
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

const CHIP: Record<string, string> = {
  paid: 'bg-green-100 text-green-800', in_transit: 'bg-blue-100 text-blue-800', pending: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800', canceled: 'bg-gray-100 text-gray-700',
};
const typeWords = (t: string) => ({ charge: 'Card payment', payment: 'Payment', refund: 'Refund', payment_refund: 'Refund', dispute: 'Dispute',
  stripe_fee: 'Stripe fee', adjustment: 'Adjustment', application_fee: 'Fee' } as Record<string, string>)[t] || t.replace(/_/g, ' ');

function PayoutLine({ p }: { p: PayoutRow }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useQuery(['payout', p.id], () => api.get(`/payouts/${p.id}`).then(r => r.data as Detail),
    { enabled: open, retry: false, staleTime: 10 * 60 * 1000, refetchOnWindowFocus: false });
  const landed = p.status === 'paid';
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 py-2 text-left">
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? '' : '-rotate-90'}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-gray-900">
            <span className="font-medium">{money(p.amount)}</span>
            <span className="text-gray-500"> · {landed ? 'landed' : p.status === 'failed' || p.status === 'canceled' ? p.words.toLowerCase() : 'lands'} {day(p.arrival)}</span>
            {p.automatic === false && <span className="text-gray-400"> · started by hand</span>}
          </div>
          <div className="text-xs text-gray-500">Sent {when(p.created)}{p.failure ? ` · ${p.failure}` : ''}</div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${CHIP[p.status] || 'bg-gray-100 text-gray-700'}`}>{p.words}</span>
      </button>
      {open && (
        <div className="pl-7 pb-3">
          {isLoading && <div className="text-xs text-gray-500">Asking Stripe…</div>}
          {!!error && <div className="text-xs text-red-700">{(error as any)?.response?.data?.message || 'Could not read that payout'}</div>}
          {data && (
            <div className="space-y-1">
              {data.lines.length === 0 && <div className="text-xs text-gray-500">Stripe lists nothing inside this payout.</div>}
              {data.lines.map(l => (
                <div key={l.id} className="flex items-baseline gap-2 text-sm">
                  <span className="text-xs text-gray-400 w-12 shrink-0">{when(l.created)}</span>
                  <span className="flex-1 min-w-0 truncate">
                    {l.customer_id ? <Link to={`/customers/${l.customer_id}`} className="text-brand-700 hover:underline">{l.customer_name}</Link>
                      : <span className="text-gray-700">{typeWords(l.type)}{l.description ? ` — ${l.description}` : ''}</span>}
                    {l.invoice_id && <> · <Link to={`/billing?invoice=${l.invoice_id}`} className="text-gray-600 hover:underline">{l.invoice_number}</Link></>}
                    {l.tip > 0 && <span className="text-gray-500"> · tip {money(l.tip)}</span>}
                    {l.card && <span className="text-gray-400"> · {l.card.brand} ···· {l.card.last4}</span>}
                  </span>
                  <span className={`tabular-nums ${l.amount < 0 ? 'text-red-700' : 'text-gray-900'}`}>{money(l.amount)}</span>
                  <span className="tabular-nums text-xs text-gray-500 w-16 text-right">{l.fee ? `−${money(l.fee)}` : ''}</span>
                </div>
              ))}
              {data.lines.length > 0 && (
                <div className="flex items-baseline gap-2 text-sm pt-1 border-t border-gray-100">
                  <span className="flex-1 text-gray-600">{plural(data.totals.lines, 'line')} · {money(data.totals.gross)} in, Stripe took {money(data.totals.fees)}</span>
                  <span className="font-medium tabular-nums">{money(data.totals.net)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PayoutsCard() {
  const [showAll, setShowAll] = useState(false);
  const { data, isFetching, refetch } = useQuery('billing-payouts', () => api.get('/payouts').then(r => r.data as Overview),
    { retry: false, staleTime: 60 * 1000, refetchOnWindowFocus: false });
  if (!data) return null;

  const summary = !data.configured
    ? <span className="text-gray-600">{data.words || 'Stripe is not connected'}</span>
    : <span>
        <span className="font-medium">{money(data.balance?.available)}</span> available
        {' · '}<span className="font-medium">{money(data.in_transit)}</span> on the way
        {data.last_paid && <> · last payout {money(data.last_paid.amount)} landed {day(data.last_paid.arrival)}</>}
      </span>;

  const payouts = data.payouts || [];
  const shown = showAll ? payouts : payouts.slice(0, 8);
  const months = (data.months || []).slice(0, 4);

  return (
    <FoldCard storageKey="billing_fold_payouts" title="Stripe payouts" summary={summary} icon={<Landmark className="w-5 h-5 text-brand-600" />}>
      {!data.configured ? (
        <p className="text-sm text-gray-600">{data.words}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500">Available at Stripe now</div>
              <div className="text-xl font-semibold">{money(data.balance?.available)}</div>
              <div className="text-xs text-gray-500">goes out on the next payout</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500">On the way to the bank</div>
              <div className="text-xl font-semibold">{money(data.in_transit)}</div>
              <div className="text-xs text-gray-500">
                {data.next ? `${plural(data.in_transit_count || 0, 'payout')} · next lands ${day(data.next.arrival)}` : 'nothing in transit'}
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500">Still settling at Stripe</div>
              <div className="text-xl font-semibold">{money(data.balance?.pending)}</div>
              <div className="text-xs text-gray-500">charged, not yet available</div>
            </div>
          </div>

          <div className="text-xs text-gray-500">
            {data.schedule ? `Payouts: ${data.schedule.toLowerCase()}` : 'Payout schedule: not read'}
            {data.bank?.last4 ? ` · to ${data.bank.bank || 'the bank'} ···· ${data.bank.last4}` : ''}
            {' · '}{plural(data.paid_count || 0, 'payout')} landed in the last {data.days} days, {money(data.paid_out)}
            {data.mode === 'test' ? ' · TEST key — not real money' : ''}
          </div>

          {!!data.failed?.length && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {data.failed.map(f => <div key={f.id}>Payout of {money(f.amount)} for {day(f.arrival)} failed{f.failure ? `: ${f.failure}` : ''} — fix the bank account in the Stripe dashboard.</div>)}
            </div>
          )}

          {months.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">By month — what came in through Stripe</div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-500"><th className="text-left font-normal">Month</th><th className="text-right font-normal">Paid in</th><th className="text-right font-normal">Refunds</th><th className="text-right font-normal">Stripe fees</th><th className="text-right font-normal">Yours</th></tr></thead>
                <tbody>
                  {months.map(m => (
                    <tr key={m.month} className="border-t border-gray-100">
                      <td className="py-1">{m.label} <span className="text-xs text-gray-400">· {plural(m.count, 'payment')}</span></td>
                      <td className="py-1 text-right tabular-nums">{money(m.gross)}</td>
                      <td className="py-1 text-right tabular-nums text-gray-500">{m.refunds ? `−${money(m.refunds)}` : '—'}</td>
                      <td className="py-1 text-right tabular-nums text-gray-500">−{money(m.fees)}</td>
                      <td className="py-1 text-right tabular-nums font-medium">{money(m.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-medium text-gray-500">Payouts — tap one to see which bills are in it</div>
              <button type="button" onClick={() => refetch()} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1" disabled={isFetching}>
                <RefreshCw className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} /> {data.fetched_at ? `as of ${fmtTs(data.fetched_at, { hour: 'numeric', minute: '2-digit' })}` : 'refresh'}
              </button>
            </div>
            {payouts.length === 0 && <div className="text-sm text-gray-500">No payouts in the last {data.days} days.</div>}
            {shown.map(p => <PayoutLine key={p.id} p={p} />)}
            {payouts.length > shown.length && (
              <button type="button" onClick={() => setShowAll(true)} className="text-xs text-brand-700 hover:underline mt-1">Show all {payouts.length}</button>
            )}
          </div>

          {!!data.errors?.length && <div className="text-xs text-amber-700">Stripe did not answer everything: {data.errors.join(' · ')}</div>}
          <a href="https://dashboard.stripe.com/payouts" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
            <ExternalLink className="w-3 h-3" /> Open payouts in the Stripe dashboard
          </a>
        </>
      )}
    </FoldCard>
  );
}
